import { isRichtext, Richtext } from "./components/common/inputs/RichTextInput";
import { FormatDate, MaxDateConst, MinDateConst, toDate } from "./components/utils/common";
import { Dictionary, MaybeDate } from "./entities/common";
import validator from 'validator';

export enum ValidatorType {
    main,
    max,
    min,
    required,
    int32,
    decimal,
    date,
    maxLength,
    step,
    greaterThenOrEqualMinDate,
    lessThenOrEqualMaxDate,
    customDate,
    person,
    email,
    unique,
    uniqueDate,
    phone,
    regexp,
    url,
    equals,
    pssToken
}

export interface IValidator {
    type: ValidatorType;
    has: (validatorType: ValidatorType) => boolean;
    isValid: (value: any) => boolean;
    getErrorMessage: (value: any) => string | undefined;
}

export class Validator implements IValidator {
    private _validators: IValidator[];

    constructor(validators: IValidator[]) {
        this._validators = validators;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.main;

    has(validatorType: ValidatorType) {
        return this._validators.some(_ => _.type === validatorType);
    }

    isValid(value: any): boolean {
        return !this._validators.some(_ => !_.isValid(value));
    }

    getErrorMessage(value: any): string | undefined {
        let error: string | undefined = undefined;
        for (let i = 0; i < this._validators.length && !error; ++i) {
            error = this._validators[i].getErrorMessage(value);
        }

        return error;
    }

    public static new(): ValidationBuilder {
        return new ValidationBuilder();
    }

    public static isValid(validator: Dictionary<IValidator>, entity: Dictionary<any>) {
        for (const property in validator) {
            if (validator.hasOwnProperty(property) && !validator[property].isValid(entity[property])) {
                return false;
            }
        }

        return true;
    }
}

export class ValidationBuilder {
    private validators: IValidator[] = [];

    public add(validator: IValidator) {
        this.validators.push(validator);
        return this;
    }

    public required(message?: string): ValidationBuilder {
        this.validators.push(new RequiredValidator(message));
        return this;
    }

    public int32(message?: string): ValidationBuilder {
        this.validators.push(new Int32Validator(message));
        return this;
    }

    public decimal(message?: string): ValidationBuilder {
        this.validators.push(new DecimalValidator(message));
        return this;
    }

    public date(message?: string): ValidationBuilder {
        this.validators.push(new DateValidator(message));
        return this;
    }

    public dateIsLessThenOrEqual(maxDate?: MaybeDate, message?: string) {
        const date = toDate(maxDate);
        if (date) {
            this.validators.push(new LessThenOrEqualMaxDateValidator(date.getEndOfDay(), message));
        }
        return this;
    }

    public dateIsGreaterThenOrEqual(minDate?: MaybeDate, message?: string) {
        const date = toDate(minDate);
        if (date) {
            this.validators.push(new GreaterThenOrEqualMinDateValidator(date.getBeginOfDay(), message));
        }
        return this;
    }

    public min(min: number, message?: string): ValidationBuilder {
        this.validators.push(new MinValidator(min, message));
        return this;
    }

    public max(max: number, message?: string): ValidationBuilder {
        this.validators.push(new MaxValidator(max, message));
        return this;
    }

    public maxLength(max: number, message?: string): ValidationBuilder {
        this.validators.push(new MaxLengthValidator(max, message));
        return this;
    }

    public step(min: number, step: number, message?: string): ValidationBuilder {
        this.validators.push(new StepValidator(min, step, message));
        return this;
    }

    public person(message?: string): ValidationBuilder {
        this.validators.push(new PersonValidator(message));
        return this;
    }

    public email(message?: string): ValidationBuilder {
        this.validators.push(new EmailValidator(message));
        return this;
    }

    public unique(values: string[], message?: string) {
        this.validators.push(new UniqueValidator(values, message));
        return this;
    }

    public uniqueDate(values: Date[], message?: string) {
        this.validators.push(new UniqueDateValidator(values, message));
        return this;
    }

    public customDate(fnValidate: (value: Date) => boolean, message?: string) {
        this.validators.push(new CustomDateValidator(fnValidate, message));
        return this;
    }

    public custom(fnValidate: (value: any) => boolean, message?: string) {
        this.validators.push(new CustomValidator(fnValidate, message));
        return this;
    }

    public equals(value: any, message?: string, not?: boolean) {
        this.validators.push(new EqualsValidator(value, message, not));
        return this;
    }

    public url() {
        this.validators.push(new UrlValidator({ host_blacklist: PRIVATE_HOST_PATTERNS }));
        return this;
    }
    
    public urlHttps() {
        this.validators.push(new UrlValidator({ require_tld: false, require_protocol: true, protocols: ['https'], host_blacklist: [...PRIVATE_NETWORK_PATTERNS, SINGLE_LABEL_NON_LOCALHOST_PATTERN] }));
        return this;
    }

    public phone() {
        this.validators.push(new PhoneValidation());
        return this;
    }

    public regExp(pattern: string | RegExp, flags?: string | undefined, message?: string) {
        this.validators.push(new RegexpValidation(pattern, flags, message));
        return this;
    }

    public noPictographicCharacters() {
        this.validators.push(new RegexpValidation(/^(?!.*[\p{Extended_Pictographic}\p{Emoji_Presentation}]).*$/gu, undefined, "Pictographic characters are not allowed"));
        return this;
    }

    public build(): Validator {
        return new Validator(this.validators);
    }
}

class RequiredValidator implements IValidator {
    private _errorMessage: string;

    constructor(message?: string) {
        this._errorMessage = message === undefined ? 'This field is required' : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.required;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: any): boolean {
        if (typeof value == "string") {
            return !!value?.trim();
        }

        if (typeof value == "boolean") {
            return true;
        }

        if (Array.isArray(value)){
            return value.length > 0;
        }

        if (isRichtext(value)) {
            return !!(value as Richtext)?.html?.replace(/&nbsp;/g, " ").replace(/(<([^>]+)>)/ig, "").trim();
        }

        return value != undefined;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    };
}

class Int32Validator implements IValidator {
    private _errorMessage: string;
    private int32MaxValue = 2147483647;

    constructor(message?: string) {
        this._errorMessage = message == undefined ? 'Invalid value' : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.int32;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: any): boolean {
        if (value === undefined)
            return true;

        return this.isIntNumber(value) && this.isLessThenMax(value) && this.isGreaterThenMin(value) || !value;
    }

    getErrorMessage(value: any): string | undefined {
        if (this.isValid(value)) {
            return '';
        } else if (!this.isIntNumber(value)) {
            return "Invalid value";
        } else if (!this.isLessThenMax(value)) {
            return `Value is greater than ${this.int32MaxValue}`;
        } else if (!this.isGreaterThenMin(value)) {
            return `Value is less than ${-this.int32MaxValue}`;
        }

        return this._errorMessage;
    };

    private isIntNumber(value: any): boolean {
        const number = Number(value);
        return !Number.isNaN(number) &&
            parseInt(value) == number &&
            !isNaN(parseInt(value, 10));
    }

    private isLessThenMax(value: any): boolean {
        const number = Number(value);
        return !Number.isNaN(number) && number <= this.int32MaxValue;
    }
    private isGreaterThenMin(value: any): boolean {
        const number = Number(value);
        return !Number.isNaN(number) && number >= -this.int32MaxValue;
    }
}

class DecimalValidator implements IValidator {
    private _errorMessage: string;

    constructor(message?: string) {
        this._errorMessage = message == undefined ? 'Invalid value' : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.decimal;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: any): boolean {
        if (value === undefined)
            return true;

        if (typeof (value) == 'string' && (value.endsWith(".") || value.endsWith(",")))
            return false;

        const number = Number(value);
        return !Number.isNaN(number);
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    };
}

class DateValidator implements IValidator {
    private _errorMessage: string;

    constructor(message?: string) {
        this._errorMessage = message == undefined ? 'Invalid value' : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.date;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: any): boolean {
        const errorMessage = this.getErrorMessage(value);
        return errorMessage === '';
    }

    getErrorMessage(value: any): string | undefined {
        if (value === undefined || value === null)
            return '';

        const date = Date.parse(value);
        if (isNaN(date)) return this._errorMessage;
        if (date < MinDateConst.getTime()) return `Value should be greater or equal to ${FormatDate(MinDateConst)}`;
        if (date > MaxDateConst.getTime()) return `Value should be less or equal to ${FormatDate(MaxDateConst)}`;
        return '';
    };
}

class MinValidator implements IValidator {
    private _min: number;
    private _errorMessage?: string;

    constructor(min: number, errorMessage?: string) {
        this._min = min;
        this._errorMessage = errorMessage;
    }

    type = ValidatorType.min;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid = (value: number) => value == null || value == undefined || (value >= this._min);
    getErrorMessage = (value: number) => this.isValid(value) ? '' : (this._errorMessage || `Value must be greater than or equal to ${this._min}`);
}

class MaxValidator implements IValidator {
    private _max: number;
    private _errorMessage?: string;

    constructor(max: number, errorMessage?: string) {
        this._max = max;
        this._errorMessage = errorMessage;
    }

    type = ValidatorType.max;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid = (value: number) => value == null || value == undefined || (value <= this._max);
    getErrorMessage = (value: number) => this.isValid(value) ? '' : (this._errorMessage || `Value must be less than or equal to ${this._max}`);
}

class MaxLengthValidator implements IValidator {
    private _max: number;
    private _errorMessage?: string;

    constructor(max: number, errorMessage?: string) {
        this._max = max;
        this._errorMessage = errorMessage;
    }

    type = ValidatorType.maxLength;
    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid = (value: string) => value == null || typeof value === 'string' && value.length <= this._max;
    getErrorMessage = (value: string) => this.isValid(value) ? '' : (this._errorMessage || `The maximum length of the field is ${this._max} characters`);
}

class StepValidator implements IValidator {
    private _step: number;
    private _min: number;
    private _errorMessage?: string;

    constructor(min: number, step: number, errorMessage?: string) {
        this._min = min;
        this._step = step;
        this._errorMessage = errorMessage;
    }

    type = ValidatorType.step;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid = (value: number) => value == null || value == undefined ||
        (this._step % 1 == 0
            ? (value - this._min) % this._step == 0
            : (((value - this._min) * (1 / this._step)) % (this._step * (1 / this._step))) * this._step == 0);
    getErrorMessage = (value: number) => this.isValid(value) ? '' : (this._errorMessage || `Value is not reachable with step ${this._step}`);
}

class GreaterThenOrEqualMinDateValidator implements IValidator {
    private _errorMessage: string;
    private minDate: Date | undefined;

    constructor(minDate: Date | undefined, message?: string) {
        this.minDate = minDate;
        this._errorMessage = this.minDate
            ? (message ?? 'Value should be greater or equal to {0}').format(FormatDate(this.minDate)!)
            : "Min value is not set";

        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.greaterThenOrEqualMinDate;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string | Date): boolean {
        if (value === undefined || value === null || !this.minDate)
            return true;

        const valueDate = toDate(value);
        if (!valueDate || isNaN(valueDate.getTime())) return false;

        return valueDate >= this.minDate;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    }
}
class LessThenOrEqualMaxDateValidator implements IValidator {
    private _errorMessage: string;
    private maxDate: Date | undefined;

    constructor(minDate: Date | undefined, message?: string) {
        this.maxDate = minDate;
        this._errorMessage = message === undefined
            ? (this.maxDate
                ? (`Value should be less or equal to ${FormatDate(this.maxDate)}`)
                : "Max value is not set")
            : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.lessThenOrEqualMaxDate;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string | Date): boolean {
        if (value === undefined || value === null || !this.maxDate)
            return true;

        const valueDate = toDate(value);
        if (!valueDate || isNaN(valueDate.getTime())) return false;

        return valueDate <= this.maxDate;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    }
}

class CustomDateValidator implements IValidator {
    private _fnValidate: (value: Date) => boolean;
    private _errorMessage: string;

    constructor(fnValidate: (value: Date) => boolean, message?: string) {
        this._fnValidate = fnValidate;
        this._errorMessage = message || 'Date is not valid';

        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.customDate;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string | Date): boolean {
        if (value === undefined || value === null)
            return true;

        const valueDate = toDate(value);
        if (!valueDate || isNaN(valueDate.getTime())) return false;

        return this._fnValidate(valueDate);
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    }
}

class CustomValidator implements IValidator {
    private _fnValidate: (value: any) => boolean;
    private _errorMessage: string;

    constructor(fnValidate: (value: any) => boolean, message?: string) {
        this._fnValidate = fnValidate;
        this._errorMessage = message || 'Value is not valid';

        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.customDate;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: any): boolean {
        if (value === undefined || value === null)
            return true;

        return this._fnValidate(value);
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    }
}

class PersonValidator implements IValidator {
    private _errorMessage: string;

    constructor(message?: string) {
        this._errorMessage = message == undefined ? 'Invalid value' : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.person;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: any): boolean {
        return !value || !!value.id;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    }
}

class EmailValidator implements IValidator {
    private _errorMessage: string;

    constructor(message?: string) {
        this._errorMessage = message == undefined ? 'not a valid email address' : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.email;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string): boolean {
        const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(value);
    }

    getErrorMessage(value: string): string | undefined {
        return this.isValid(value) ? '' : (`'${value}' ${this._errorMessage}`);
    }
}

class UniqueValidator implements IValidator {
    private _errorMessage: string;
    private values: string[];

    constructor(values: string[], message?: string) {
        this.values = values.map(_ => _.toLowerCase());
        this._errorMessage = message == undefined ? "already exists" : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.unique;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string): boolean {
        if (value === undefined || value === null)
            return true;

        return this.values.indexOf(value.toLowerCase()) === -1;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : (`'${value}' ${this._errorMessage}`);
    }
}

class UniqueDateValidator implements IValidator {
    private _errorMessage: string;
    private values: Date[];

    constructor(values: Date[], message?: string) {
        this.values = values.map(_ => _.getBeginOfDay());
        this._errorMessage = message == undefined ? "The selected date is not unique" : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.uniqueDate;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: Date): boolean {
        if (value === undefined || value === null)
            return true;

        return this.values.map(Number).indexOf(+value.getBeginOfDay()) === -1;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : this._errorMessage;
    }
}

class EqualsValidator implements IValidator {
    private _errorMessage: string;
    private value: any;
    private _not?: boolean;

    constructor(value: any, message?: string, not?: boolean) {
        this.value = value;
        this._not = not;
        this._errorMessage = message == undefined ? "is not expected" : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.equals;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string): boolean {
        return this._not ? value !== this.value : value === this.value;
    }

    getErrorMessage(value: any): string | undefined {
        return this.isValid(value) ? '' : (`'${value}' ${this._errorMessage}`);
    }
}

// Private network ranges (excluding loopback); also rejected by urlHttps() so that only localhost is allowed.
const PRIVATE_NETWORK_PATTERNS: RegExp[] = [
    /^10(\.\d{1,3}){3}$/,
    /^169\.254(\.\d{1,3}){2}$/,
    /^192\.168(\.\d{1,3}){2}$/,
    /^172\.(1[6-9]|2\d|3[01])(\.\d{1,3}){2}$/,
];

// require_tld: false admits any single-label host (e.g. https://intranet); only localhost is allowed among them.
const SINGLE_LABEL_NON_LOCALHOST_PATTERN = /^(?!localhost$)[^.]+$/i;

const PRIVATE_HOST_PATTERNS: RegExp[] = [
    /^localhost$/i,
    /^127(\.\d{1,3}){3}$/,
    ...PRIVATE_NETWORK_PATTERNS,
];

class UrlValidator implements IValidator {
    type = ValidatorType.url;
    private _options?: validator.IsURLOptions;

    constructor(options?: validator.IsURLOptions) {
        this._options = options;
    }

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string): boolean {
        if (!value) {
            return true;
        }
        return validator.isURL(value, this._options);
    }

    getErrorMessage(value: string): string {
        return this.isValid(value) ? "" : "Invalid URL";
    }
}

class PhoneValidation implements IValidator {
    type = ValidatorType.phone;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string): boolean {
        return /^[+]*[0-9]{0,3}[(]{0,1}[0-9]{0,4}[)]{0,1}[-\s\./0-9]{7,15}$/i.test(value);
    }

    getErrorMessage(value: any): string {
        return this.isValid(value) ? "" : "Invalid phone number";
    }
}

class RegexpValidation implements IValidator {
    private _pattern: string | RegExp;
    private _flags?: string;
    private _errorMessage: string;

    constructor(pattern: string | RegExp, flags?: string | undefined, message?: string) {
        this._pattern = pattern;
        this._flags = flags;
        this._errorMessage = message == undefined ? "Invalid value" : message;
        this.isValid = this.isValid.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
    }

    type = ValidatorType.regexp;

    has(validatorType: ValidatorType) {
        return this.type === validatorType;
    }

    isValid(value: string): boolean {
        if (!value) {
            return true;
        }
        
        return new RegExp(this._pattern, this._flags).test(value);
    }

    getErrorMessage(value: any): string {
        return this.isValid(value) ? "" : this._errorMessage;
    }
}